/**
 * Welle 12 (pre139): Gemeinsame Komponente für die „Eingehender Anruf"-Karte.
 *
 * Genutzt von der A/V-Ansicht (views/Incoming.tsx) UND vom Companion
 * (views/Companion.tsx, fernsteuernd). So ist die Optik pixelgleich; die
 * unterschiedliche Verdrahtung (lokal annehmen vs. Annehmen am A/V-Gerät
 * fernauslösen) liegt allein in den Handlern der jeweiligen Seite.
 *
 * Die Komponente rendert NUR die innere `.incoming-card`. Den Rahmen
 * (A/V: `.incoming-stage`, Companion: Vollbild-Overlay) liefert die aufrufende
 * Seite — die beiden Kontexte unterscheiden sich bewusst.
 */
import type React from 'react';
import { barBtn } from './controlBarStyles';
import { useUIStore } from '@/state/store';
import { getRingtone } from '@/hooks/useAudio';

export interface IncomingCallCardProps {
  steleName: string;
  tenantName?: string;
  group?: string;
  strategy?: string;
  /** A/V kennt die Wartezeit; Companion lässt sie weg. */
  waitingSec?: number | null;
  onAccept: () => void;
  onDecline: () => void;
  /** Companion-spezifischer Hinweistext unter den Buttons. */
  footerHint?: string;
  /** Companion-spezifisch: „Abdocken"-Knopf für das schwebende PiP-Fenster. */
  dock?: { supported: boolean; active: boolean; onToggle: () => void };
}

export function IncomingCallCard(props: IncomingCallCardProps) {
  const { steleName, tenantName, group, strategy, waitingSec, onAccept, onDecline, footerHint, dock } = props;
  const ringtoneEnabled = useUIStore((s) => s.audioPrefs.ringtone);
  const ringtoneLabel = getRingtone(useUIStore((s) => s.audioPrefs.ringtoneId)).label;
  return (
    <div className="incoming-card">
      <div className="incoming-eyebrow">
        <span className="in-dot" />
        Eingehender Anruf
      </div>
      <div className="incoming-from">
        <div className="incoming-stele">{steleName}</div>
        {tenantName ? <div className="incoming-tenant">{tenantName}</div> : null}
      </div>
      <div className="incoming-meta">
        {group ? <span>{group}</span> : null}
        {strategy ? <span><strong>{strategy}</strong></span> : null}
        {typeof waitingSec === 'number' ? <span>Wartet seit <strong>{waitingSec}s</strong></span> : null}
      </div>
      <div className="incoming-actions">
        <button className="btn-decline-call" onClick={onDecline} title="Ablehnen">
          <svg viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
        <button className="btn-accept-call" onClick={onAccept} title="Annehmen">
          <svg viewBox="0 0 24 24">
            <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 0 0-1.02.24l-2.2 2.2a15.074 15.074 0 0 1-6.59-6.58l2.2-2.21c.27-.27.35-.66.24-1.02C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z" />
          </svg>
        </button>
      </div>
      {dock && dock.supported && !dock.active ? (
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <button onClick={dock.onToggle} style={{ ...barBtn('normal'), fontSize: 11.5 }} title="Eingehenden Anruf in ein schwebendes Fenster abdocken">Abdocken</button>
        </div>
      ) : null}
      <div className="incoming-shortcut"><kbd>↵</kbd> annehmen · <kbd>Esc</kbd> ablehnen</div>
      <div className="ring-anim">{ringtoneEnabled ? `Klingelt · ${ringtoneLabel}` : 'Stumm'}</div>
      {footerHint ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, textAlign: 'center' }}>{footerHint}</div>
      ) : null}
    </div>
  );
}
